import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import CloseIcon from '@/assets/icons/close.svg?react';
import InfoIcon from '@/assets/icons/info.svg?react';
import { dialogLocators } from '@/containers/dialog-container/dialog.locators';
import { cn } from '@/core/string.core';
import { Tooltip } from '@/containers/tooltip-container/tooltip.wrapper';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-40 bg-modal-backdrop backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ElementRef<typeof DialogPrimitive.Content>;

const DialogContent = React.forwardRef<
  DialogContentProps,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay data-testid={dialogLocators.dialogCloseOverlay} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%]',
        'bg-white-text rounded p-4 border border-other-stroke focus-visible:outline-none',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'flex flex-col',
        // closing animations are disabled since we're unmounting the dialog before the animation ends
        // in case you want to use them, you'll need to add the following classes to the dialog content
        // "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        // "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
        className,
      )}
      onCloseAutoFocus={(event) => {
        // Prevent automatic focus restoration to avoid page jumping
        event.preventDefault();
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        role="button"
        aria-label="Close dialog"
        data-testid={dialogLocators.dialogCloseBtn}
        className={cn(
          'w-8 h-8 absolute right-4 top-4 flex items-center justify-center rounded-full',
          'bg-light-gray border border-light-gray',
          'focus:border-primary focus:outline-none ',
          'hover:border-other-stroke-hover',
          'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-1',
        )}
      >
        <CloseIcon role="img" aria-label="dialog close icon" className="h-3 w-3 " />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { titleTooltip?: React.ReactNode }
>(({ className, titleTooltip, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} asChild {...props}>
    <h2 className={cn('leading-8 mb-5 flex items-center gap-2', className)}>
      {props.children}
      {titleTooltip && (
        <Tooltip content={titleTooltip} position={{ side: 'right' }} className="max-w-xs text-sm">
          <span>
            <InfoIcon className="h-4 w-4" aria-label="More information" />
          </span>
        </Tooltip>
      )}
    </h2>
  </DialogPrimitive.Title>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
